<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  fetchComplianceRules,
  fetchComplianceRuleById,
  createComplianceRule,
  updateComplianceRule,
  deleteComplianceRule,
} from "@/services/backofficeClient";
import ComplianceActionsEditor from "@/components/ComplianceActionsEditor.vue";
import { ROLES } from "@/services/acl";

const store = useStore();
const canCreateRule = computed(() =>
  store.getters.hasRole(ROLES.COMPLIANCE_RULE_CREATE)
);
const canEditRule = computed(() =>
  store.getters.hasRole(ROLES.COMPLIANCE_RULE_EDIT)
);
const canDeleteRule = computed(() =>
  store.getters.hasRole(ROLES.COMPLIANCE_RULE_DELETE)
);

const rules = ref([]);
const rulesLoading = ref(true);
const rulesError = ref("");

const selectedRuleId = ref(null);
const saving = ref(false);
const deleting = ref(false);
const formMessage = ref("");
const formMessageType = ref("success");
const conditionTemplateKey = ref("");
const actionsEditorRef = ref(null);

const CONDITION_TEMPLATES = [
  {
    key: "email-domain",
    label: "Email domain matches",
    value: {
      type: "FieldPattern",
      field: "email",
      pattern: ".*@example.com$",
    },
  },
  {
    key: "and-media",
    label: "Name AND media label",
    value: {
      type: "AndCondition",
      conditions: [
        {
          type: "FieldPattern",
          field: "name",
          pattern: "fraud|spam",
        },
        {
          type: "MediaLabel",
          label: "violence",
          valuePattern: null,
          minScore: 0.6,
        },
      ],
    },
  },
  {
    key: "or-phone-email",
    label: "Phone OR email pattern",
    value: {
      type: "OrCondition",
      conditions: [
        { type: "FieldPattern", field: "email", pattern: "tempmail" },
        { type: "FieldPattern", field: "phone", pattern: "\\+?99" },
      ],
    },
  },
];

const DEFAULT_CONDITION = CONDITION_TEMPLATES[0].value;
const DEFAULT_ACTIONS = [];

const form = reactive({
  name: "",
  isActive: true,
  conditionInput: "",
  createResolvedComplaint: false,
  activeUsersOnly: true,
  prompt: "",
});

const isEditing = computed(() => Boolean(selectedRuleId.value));
const isEmptyList = computed(
  () => !rulesLoading.value && !rulesError.value && rules.value.length === 0
);
const hasPrompt = computed(() => !!(form.prompt || "").trim());

watch(hasPrompt, (promptSet) => {
  if (promptSet) {
    form.createResolvedComplaint = false;
  }
});

const safeStringify = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return "";
  }
};

const normalizeRule = (rule) => ({
  id: rule?.id || rule?._id || "",
  name: rule?.name || "",
  condition: rule?.condition,
  actions: Array.isArray(rule?.actions) ? rule.actions : [],
  isActive: rule?.isActive !== false,
  createResolvedComplaint: rule?.createResolvedComplaint || false,
  activeUsersOnly: rule?.activeUsersOnly !== false,
  prompt: rule?.prompt || "",
});

const applyRuleToForm = (rule) => {
  selectedRuleId.value = rule?.id || rule?._id || null;
  form.name = rule?.name || "";
  form.isActive = rule?.isActive !== false;
  form.createResolvedComplaint = rule?.createResolvedComplaint || false;
  form.activeUsersOnly = rule?.activeUsersOnly !== false;
  form.conditionInput = safeStringify(rule?.condition || DEFAULT_CONDITION);
  form.prompt = rule?.prompt || "";
  actionsEditorRef.value?.reset(rule?.actions);
  formMessage.value = "";
  formMessageType.value = "success";
};

const resetForm = () => {
  selectedRuleId.value = null;
  form.name = "";
  form.isActive = true;
  form.createResolvedComplaint = false;
  form.activeUsersOnly = true;
  form.conditionInput = safeStringify(DEFAULT_CONDITION);
  form.prompt = "";
  actionsEditorRef.value?.reset(DEFAULT_ACTIONS);
  formMessage.value = "";
  formMessageType.value = "success";
  conditionTemplateKey.value = "";
};

const loadRules = async () => {
  rulesLoading.value = true;
  rulesError.value = "";
  try {
    const payload = await fetchComplianceRules();
    const list = Array.isArray(payload) ? payload : [];
    rules.value = list.map(normalizeRule);
  } catch (error) {
    console.error("Failed to fetch compliance rules", error);
    rulesError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load compliance rules.";
  } finally {
    rulesLoading.value = false;
  }
};

const selectRule = async (rule) => {
  if (!rule?.id) return;
  selectedRuleId.value = rule.id;
  formMessage.value = "";
  saving.value = false;
  try {
    const fullRule = await fetchComplianceRuleById(rule.id);
    applyRuleToForm(fullRule);
  } catch (error) {
    console.error("Failed to fetch compliance rule", error);
    formMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load rule details.";
    formMessageType.value = "danger";
  }
};

const summarizeCondition = (condition, depth = 0) => {
  if (!condition || typeof condition !== "object" || depth > 2) return "--";
  if (condition.type === "AndCondition" || condition.type === "OrCondition") {
    const items = Array.isArray(condition.conditions) ? condition.conditions : [];
    const preview = items
      .slice(0, 2)
      .map((c) => summarizeCondition(c, depth + 1))
      .join(" • ");
    const more = items.length > 2 ? ` +${items.length - 2}` : "";
    return `${condition.type === "AndCondition" ? "AND" : "OR"} [${preview}${more}]`;
  }
  if (condition.type === "FieldPattern") {
    return `${condition.field || "field"} ~ ${condition.pattern || "pattern"}`;
  }
  if (condition.type === "MediaLabel") {
    const parts = [condition.label || "label"];
    if (condition.valuePattern) parts.push(`~${condition.valuePattern}`);
    if (condition.minScore !== undefined && condition.minScore !== null) {
      parts.push(`score>=${condition.minScore}`);
    }
    return `Media ${parts.join(" ")}`;
  }
  return typeof condition.type === "string" ? condition.type : "--";
};

const prettifyCondition = () => {
  try {
    const parsed = JSON.parse(form.conditionInput || "{}");
    form.conditionInput = safeStringify(parsed);
    formMessage.value = "";
  } catch (error) {
    formMessage.value = "Condition JSON is invalid.";
    formMessageType.value = "danger";
  }
};

const applyConditionTemplate = () => {
  const template = CONDITION_TEMPLATES.find(
    (item) => item.key === conditionTemplateKey.value
  );
  if (!template) return;
  form.conditionInput = safeStringify(template.value);
};

const parseConditionForSubmit = () => {
  try {
    const parsed = JSON.parse(form.conditionInput || "{}");
    if (!parsed || typeof parsed !== "object") {
      throw new Error("Condition must be an object.");
    }
    if (!parsed.type || typeof parsed.type !== "string") {
      throw new Error("Condition must include a type field.");
    }
    return parsed;
  } catch (error) {
    throw new Error(error.message || "Invalid condition JSON.");
  }
};

const submitForm = async () => {
  formMessage.value = "";
  formMessageType.value = "success";
  saving.value = true;
  try {
    const name = (form.name || "").trim();
    if (!name) {
      throw new Error("Rule name is required.");
    }
    const trimmedPrompt = (form.prompt || "").trim();
    if (trimmedPrompt && form.createResolvedComplaint) {
      throw new Error(
        "Create Resolved Complaint cannot be enabled while an AI review prompt is set."
      );
    }
    const payload = {
      name,
      isActive: !!form.isActive,
      createResolvedComplaint: !trimmedPrompt && !!form.createResolvedComplaint,
      activeUsersOnly: !!form.activeUsersOnly,
      condition: parseConditionForSubmit(),
      actions: actionsEditorRef.value.getPayload(),
      prompt: trimmedPrompt || null,
    };

    let response;
    if (isEditing.value) {
      response = await updateComplianceRule(selectedRuleId.value, payload);
      formMessage.value = "Rule updated.";
    } else {
      response = await createComplianceRule(payload);
      formMessage.value = "Rule created.";
    }
    formMessageType.value = "success";

    const newId = response?.id || response?._id || selectedRuleId.value;
    await loadRules();
    if (newId) {
      const fresh = await fetchComplianceRuleById(newId).catch(() => response);
      applyRuleToForm(fresh || payload);
    } else if (!isEditing.value) {
      resetForm();
    }
  } catch (error) {
    console.error("Failed to save compliance rule", error);
    formMessage.value =
      error?.payload?.message || error?.message || "Failed to save rule.";
    formMessageType.value = "danger";
  } finally {
    saving.value = false;
  }
};

const deleteRule = async () => {
  if (!selectedRuleId.value) {
    formMessage.value = "Select a rule to delete.";
    formMessageType.value = "danger";
    return;
  }
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this compliance rule?"
  );
  if (!confirmDelete) return;

  formMessage.value = "";
  formMessageType.value = "success";
  deleting.value = true;
  try {
    await deleteComplianceRule(selectedRuleId.value);
    formMessage.value = "Rule deleted.";
    formMessageType.value = "success";
    resetForm();
    await loadRules();
  } catch (error) {
    console.error("Failed to delete compliance rule", error);
    formMessage.value =
      error?.payload?.message || error?.message || "Failed to delete rule.";
    formMessageType.value = "danger";
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  resetForm();
  await loadRules();
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12 col-lg-5">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0 d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h6 class="mb-1">Compliance Rules</h6>
              <p class="text-xs text-secondary mb-0">
                Select a rule to edit or start a new one.
              </p>
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="rulesLoading"
                @click="resetForm">
                New rule
              </button>
              <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="rulesLoading"
                @click="loadRules">
                <i class="fas fa-sync me-2"></i>
                {{ rulesLoading ? "Refreshing..." : "Refresh" }}
              </button>
            </div>
          </div>

          <div v-if="rulesLoading" class="card-body text-center py-5">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 mb-0 text-sm text-secondary">
              Loading compliance rules...
            </p>
          </div>

          <div v-else-if="rulesError" class="card-body text-center py-5">
            <div class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto">
              <i class="ni ni-fat-remove"></i>
            </div>
            <p class="mt-3 text-sm">{{ rulesError }}</p>
            <button class="btn btn-sm btn-success" type="button" @click="loadRules">
              Try again
            </button>
          </div>

          <div v-else class="card-body">
            <div v-if="isEmptyList" class="text-center py-5">
              <p class="text-sm text-secondary mb-2">No compliance rules yet.</p>
              <button class="btn btn-sm btn-success" type="button" @click="resetForm">
                Create first rule
              </button>
            </div>
            <div v-else class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Name
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Condition
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      AI Review
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Active
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rule in rules" :key="rule.id || rule.name"
                    :class="rule.id === selectedRuleId ? 'bg-light' : ''" style="cursor: pointer"
                    @click="selectRule(rule)">
                    <td>
                      <div class="d-flex flex-column">
                        <span class="text-sm font-weight-bold">
                          {{ rule.name || "Untitled" }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span class="text-xs text-secondary d-block text-truncate" style="max-width: 280px">
                        {{ summarizeCondition(rule.condition) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="badge text-xxs"
                        :class="rule.prompt ? 'bg-gradient-warning' : 'bg-gradient-secondary'">
                        {{ rule.prompt ? "AI-gated" : "Immediate" }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="badge text-xxs"
                        :class="rule.isActive ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                        {{ rule.isActive ? "Active" : "Disabled" }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-7 mt-4 mt-lg-0">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0 d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h6 class="mb-1">
                {{ isEditing ? "Edit compliance rule" : "Create compliance rule" }}
              </h6>
              <p class="text-xs text-secondary mb-0">
                Nested conditions are provided as JSON; use builder or raw JSON for actions.
              </p>
            </div>
            <div class="text-end" v-if="isEditing && selectedRuleId">
              <span class="badge bg-light text-dark border text-xxs">
                ID: {{ selectedRuleId }}
              </span>
            </div>
          </div>

          <div class="card-body">
            <div v-if="formMessage" class="alert py-2 px-3"
              :class="formMessageType === 'danger' ? 'alert-danger' : 'alert-success'">
              <span class="text-sm">{{ formMessage }}</span>
            </div>

            <div class="mb-3">
              <label class="form-label text-sm fw-semibold">Rule name</label>
              <input v-model="form.name" type="text" class="form-control" placeholder="e.g. Block suspicious media"
                :disabled="saving" />
            </div>

            <div class="form-check form-switch mb-4">
              <input class="form-check-input" type="checkbox" id="ruleIsActive" v-model="form.isActive"
                :disabled="saving" />
              <label class="form-check-label text-sm" for="ruleIsActive">Active</label>
            </div>

            <div class="mb-4">
              <div class="form-check form-switch mb-0">
                <input class="form-check-input" type="checkbox" id="ruleCreateResolvedComplaint"
                  v-model="form.createResolvedComplaint" :disabled="saving || hasPrompt" />
                <label class="form-check-label text-sm" for="ruleCreateResolvedComplaint">Create Resolved
                  Complaint</label>
              </div>
              <p v-if="hasPrompt" class="text-xs text-secondary mb-0">
                Not available while an AI review prompt is set below — AI-gated matches always
                start as a pending complaint.
              </p>
            </div>

            <div class="form-check form-switch mb-4">
              <input class="form-check-input" type="checkbox" id="ruleActiveUsersOnly" v-model="form.activeUsersOnly"
                :disabled="saving" />
              <label class="form-check-label text-sm" for="ruleActiveUsersOnly">Active users only</label>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
              <label class="form-label text-sm fw-semibold mb-0">Condition (JSON)</label>
              <div class="d-flex gap-2 flex-wrap">
                <select class="form-select form-select-sm" style="min-width: 200px" v-model="conditionTemplateKey"
                  :disabled="saving" @change="applyConditionTemplate">
                  <option value="">Insert sample</option>
                  <option v-for="template in CONDITION_TEMPLATES" :key="template.key" :value="template.key">
                    {{ template.label }}
                  </option>
                </select>
                <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="saving"
                  @click="prettifyCondition">
                  Prettify
                </button>
              </div>
            </div>
            <textarea v-model="form.conditionInput" class="form-control font-monospace" rows="8" :disabled="saving"
              placeholder='{ "type": "FieldPattern", "field": "email", "pattern": ".*@example.com$" }'></textarea>
            <p class="text-xs text-secondary mt-1 mb-3">
              Supported condition types: AndCondition / OrCondition (with "conditions" array),
              FieldPattern (field: email, phone, name, atom, deviceId, timezone + pattern),
              MediaLabel (label, valuePattern?, minScore?),
            </p>

            <hr />

            <div class="mb-4">
              <label class="form-label text-sm fw-semibold">AI review prompt (optional)</label>
              <textarea v-model="form.prompt" class="form-control" rows="3" :disabled="saving"
                placeholder="e.g. The word 'برنامه' can refer to prostitution/escort services, but can also mean 'program' (e.g. programming). Only treat this as a violation if the surrounding bio text clearly implies prostitution."></textarea>
              <p class="text-xs text-secondary mt-1 mb-0">
                When set, a match no longer applies its actions immediately. Instead a pending
                complaint is created and an AI reviews it against this guidance: it resolves the
                complaint and applies the actions only if it agrees the match is a genuine
                violation, otherwise the complaint is rejected. Leave empty to keep applying
                actions immediately, as before.
              </p>
            </div>

            <ComplianceActionsEditor ref="actionsEditorRef" :disabled="saving" />

            <div class="d-flex align-items-center gap-2 mt-4">
              <button v-if="isEditing ? canEditRule : canCreateRule" class="btn btn-success mb-0" type="button"
                :disabled="saving || deleting" @click="submitForm">
                {{ saving ? "Saving..." : isEditing ? "Update rule" : "Create rule" }}
              </button>
              <button class="btn btn-outline-secondary mb-0" type="button" :disabled="saving || deleting"
                @click="resetForm">
                Clear
              </button>
              <button v-if="isEditing && canDeleteRule" class="btn btn-outline-danger mb-0 ms-auto" type="button"
                :disabled="saving || deleting" @click="deleteRule">
                {{ deleting ? "Deleting..." : "Delete rule" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
